import { Component, Element, Env, h, Listen, Prop, State, Watch } from '@stencil/core';
import { BrowserHistoryEngine, createRouter } from 'routerjs';
import { ArrayHistoryEngine } from '../../../utils/ArrayHistoryEngine';
import {
  baseUri,
  currentEnvironment,
  endpoint_api,
  endpoint_assets,
  httpV2,
  language,
  translate,
  Utils,
} from '../../../utils/utils';
import { ResizeObserver } from '@juggle/resize-observer';
import tinycolor from 'tinycolor2';
import { Settings } from 'luxon';
import * as Sentry from '@sentry/browser';
import axios, { AxiosRequestConfig } from 'axios';
import {
  customPortalExists,
  customPortalState,
  getShopAdvancedOptions,
  getShopDeveloperOptions,
  getShopLayout,
  homeStore,
  shopConfig,
  shopConfigState,
  shopIntegrationState,
  themeState,
} from '../../../utils/store';
import { Shop, ShopTheme, ShopType } from '../../../models/shop.model';
import { v4 as uuidv4 } from 'uuid';

@Component({
  tag: 'ike-layout',
  styleUrl: 'ike-layout.scss',
  shadow: true,
})
export class IkeLayout {
  @Prop({ mutable: true }) shopId: string = null;
  @Prop() mode: 'SHOP' | 'PROFILE' = 'SHOP';
  @Prop() goToRoute: string = null;
  @Prop() basePath: string = null;
  @Prop() groupUuid: string = null;
  @Prop() customPortalId: number = null;
  @Prop({ mutable: true }) standalone: boolean;
  @Prop() draft: number = 0;

  @Prop() theme: ShopTheme;
  @Prop() maincolor: string = ''; //For retro-compability
  @Prop() color: string = '';

  @Prop({ mutable: true }) isEmbedded: boolean = true;
  @Prop({ mutable: true }) interfaceLanguage: string = null;

  @Prop() preventSingleArticleRedirection : boolean = false;

  @State() route = null;
  @State() config = null;
  @State() cart = null;
  @State() loading: boolean = true;
  @State() loadingFont: boolean = true;
  @State() loadingFirst: boolean = true;
  @State() customStyle = '';
  @State() pageEl = null;
  @State() breakpoint = 'SM';
  @State() notifications = [];
  @State() isMultiPeriodSwitchEnabled = false;
  @State() customElements: string[] = [];

  @Element() host: HTMLElement;

  private utils: any;
  private unsub = [];
  private layout: any;
  private showPeriodList = false;
  private showQueue = false;
  private showDemoAlert = false;
  private firstComp: any;
  private fonts = [
    {
      name: 'ike', weight: '400', url:
        ` url('${endpoint_assets}/shop/fonts/ike.eot?mlqxpk#iefix') format('embedded-opentype'),
        url('${endpoint_assets}/shop/fonts/ike.ttf?mlqxpk') format('truetype'),
        url('${endpoint_assets}/shop/fonts/ike.woff?mlqxpk') format('woff'),
        url('${endpoint_assets}/shop/fonts/ike.svg?mlqxpk#ike') format('svg')`,
    },
    {
      name: 'Suisse Intl', weight: '400', url:
        ` url("${endpoint_assets}/shop/fonts/SuisseIntl-Regular.woff2") format("woff2"),
        url("${endpoint_assets}/shop/fonts/SuisseIntl-Regular.woff") format("woff"),
        url("${endpoint_assets}/shop/fonts/SuisseIntl-Regular.otf") format("opentype")`,
    },
    {
      name: 'Suisse Intl', weight: '500', url:
        ` url("${endpoint_assets}/shop/fonts/SuisseIntl-Book.woff2") format("woff2"),
        url("${endpoint_assets}/shop/fonts/SuisseIntl-Book.woff") format("woff"),
        url("${endpoint_assets}/shop/fonts/SuisseIntl-Book.otf") format("opentype")`,
    },
    {
      name: 'Suisse Intl', weight: '700', url:
        ` url("${endpoint_assets}/shop/fonts/SuisseIntl-Medium.woff2") format("woff2"),
        url("${endpoint_assets}/shop/fonts/SuisseIntl-Medium.woff") format("woff"),
        url("${endpoint_assets}/shop/fonts/SuisseIntl-Medium.otf") format("opentype")`,
    },
    {
      name: 'Suisse Intl', weight: '800', url:
        ` url("${endpoint_assets}/shop/fonts/SuisseIntl-SemiBold.woff2") format("woff2"),
        url("${endpoint_assets}/shop/fonts/SuisseIntl-SemiBold.woff") format("woff"),
        url("${endpoint_assets}/shop/fonts/SuisseIntl-SemiBold.otf") format("opentype")`,
    },
    {
      name: 'Suisse Intl', weight: '900', url:
        ` url("${endpoint_assets}/shop/fonts/SuisseIntl-Bold.woff2") format("woff2"),
        url("${endpoint_assets}/shop/fonts/SuisseIntl-Bold.woff") format("woff"),
        url("${endpoint_assets}/shop/fonts/SuisseIntl-Bold.otf") format("opentype")`,
    },
  ];

  private routes = [
    { path: `/guideline`, component: 'guideline-component' },

    { path: `/`, component: 'ike-home' },
    { path: `/login`, component: 'ike-login' },
    { path: `/profile`, component: 'ike-user-profile' },
    { path: `/profile/edit`, component: 'ike-user-edit' },
    { path: `/profile/orders`, component: 'ike-user-orders' },
    { path: `/profile/abos`, component: 'ike-user-abos' },
    { path: `/profile/order/:orderId`, component: 'ike-user-order' },

    { path: `/order`, component: 'ike-cart' },
    { path: `/checkout`, component: 'ike-cart-payment' },
    { path: `/delivery`, component: 'ike-cart-delivery' },
    { path: `/form`, component: 'ike-cart-form' },
    { path: `/success/:uuid`, component: 'ike-cart-success' },
    { path: `/cashless/:uuid`, component: 'ike-order-cashless' },
    { path: `/events/:eventId`, component: 'ike-event' },
    { path: `/event/:dateId`, component: 'ike-event' },
    { path: `/event/:dateId/stream`, component: 'ike-event-stream' },
    { path: `/survey/:dateId`, component: 'ike-event-forms' },
    { path: `/abo/:aboId`, component: 'ike-abo' },
    { path: `/abo/:aboId/forms`, component: 'ike-abo-forms' },
    { path: `/abo/:aboId/forms/renew`, component: 'ike-abo-forms' },
    { path: `/abo/:aboId/selection`, component: 'ike-abo-selection' },
    { path: `/pass-access`, component: 'ike-abo-code' },
    { path: `/pass-area`, component: 'ike-abo-area' },
    { path: `/pass-area/:access`, component: 'ike-abo-area' },
    { path: `/pass-resale-tickets`, component: 'ike-abo-resale-tickets' },
    { path: `/pass-renew`, component: 'ike-abo-renew' },

    { path: `/gift/:giftId`, component: 'ike-gift' },

    { path: `/product/:productId`, component: 'ike-product' },

    { path: `/exchange`, component: 'ike-exchange' },
  ];

  componentWillLoad() {
    if (this.mode === 'PROFILE') {
      shopIntegrationState.isProfile = true;
      this.isEmbedded = false;
      this.shopId = 'profile';
      this.goToRoute = 'profile'
    }

    this.utils = new Utils(this.shopId);

    this.setupCustomPortal(this.customPortalId);

    if (!this.color.trim()) {
      this.color = this.maincolor;
    }

    if(this.color || this.theme) {
      this.setupTheme(this.color, this.theme, true);
    }

    if (this.groupUuid && shopConfigState.type === ShopType.DEFAULT) {
      this.isMultiPeriodSwitchEnabled = true;
    }

    const currLang = this.interfaceLanguage ?? document.getElementsByTagName('html')[0].getAttribute('lang')?.slice(0, 2);
    if (currLang) {
      language(currLang);
    }

    shopIntegrationState.isEmbedded = this.isEmbedded;

    this.unsub.push(this.utils.cart(cart => this.loadCart(this.cart = cart)));
    const firstComp = new Promise(resolve => this.firstComp = resolve);

    const initializers = [
      this.loadFonts(),
      this.createRouter(),
    ];

    if (this.shopId) {
      initializers.push(this.getCart(), this.getConfig());

      if (!shopIntegrationState.isProfile) {
        initializers.push(this.getLayout());
      }
    }

    Promise.all(initializers).then(() => {
      this.initShop(firstComp);
    });

    shopConfig.onChange<'type'>('type', (type)=> {
      this.isMultiPeriodSwitchEnabled = this.groupUuid !== null && type === ShopType.DEFAULT
    });

    document.addEventListener('updateShopCode', this.handleShopCodeChange.bind(this));
    //Only used from within the manager on the "online-shop" page
    document.addEventListener('updateShopPreview', this.handleShopPreviewChange.bind(this));
  }

  private initShop(loadPromise: Promise<unknown> = null): void {
    this.loadCustom();

    if (loadPromise === null) {
      loadPromise = this.createRouter();
    }

    if (this.cart?.waiting) {
      this.utils.store.set('config', { ...this.config, has_queue: true });
    }

    this.initSentry();
    this.loadingFirst = false;

    homeStore.reset();

    loadPromise?.then(() => {
      this.loadingFont = false
      document.dispatchEvent(new CustomEvent('shopLoaded'));
    });

    Settings.defaultLocale = language();

    this.shouldGoToPage();
    this.shouldGoToCheckout();

    if (this.config?.has_queue) {
      setTimeout(() => axios.get(endpoint_api + '/kueue-ping', {
        headers: {
          'kueue': document.cookie.match('(^|;)\\s*kueue\\s*=\\s*([^;]+)')?.pop() || '',
        } as any,
        withCredentials: true,
      } as AxiosRequestConfig), 10 * 1000);
    }
  }

  private async handleShopCodeChange(data: { detail: string }): Promise<void> {
    this.loadingFirst = true;
    this.loadingFont = true;
    this.shopId = data.detail;

    this.utils = new Utils(this.shopId);
    this.unsub.push(this.utils.cart(cart => this.loadCart(this.cart = cart)));

    await Promise.all([this.getCart(), this.getConfig(), this.getLayout()])

    this.loadingFirst = false;
    this.loadingFont = false;

    this.initShop();
  }

  private handleShopPreviewChange(data: { detail: Shop }) {
    this.setupTheme(data.detail.color, data.detail.theme as ShopTheme, true)
    shopConfigState.layout = getShopLayout(data.detail);
    shopConfigState.advancedOptions = getShopAdvancedOptions(data.detail);
    shopConfigState.developerOptions = getShopDeveloperOptions(data.detail, true);
  }

  private getCart() {
    return this.utils.http('get', '/cart').then(cart => this.utils.store.set('cart', cart));
  }

  private loadCart(cart) {
    if (cart) {
      this.showQueue = !!cart.waiting;
      if (cart.showperiod) {
        this.showPeriodList = true;
      } else {
        this.utils.storePeriod(cart.period);
      }
      if (this.interfaceLanguage && cart?.interface_languages.filter(l => l.sLocaleShort == this.interfaceLanguage)[0].sLocaleShort) {
        this.changeInterfaceLanguage(false);
      } else {
        language(cart?.language?.short ?? 'fr');
      }
      if (cart?.uuid) {
        this.utils.storage.set('uuid', cart.uuid);
      }
    }
  }

  private getConfig() {
    // OLD Config, @see IkeLayout.getLayout
    return this.utils.http('get', '/home/config').then(config => {
      this.utils.store.set('config', this.config = config);
      if (config.redirect) {
        Sentry.captureMessage('redirecting to '+config.redirect, 'info');
        window.location.href = config.redirect;
      }
      this.showDemoAlert = !!config.is_demo;
    });
  }

  private getLayout() {
    return httpV2('get', `/shop/${this.shopId}`, {
      with: [
        'language',
        'sections.language',
        'csps',
      ]
    }).then(response => {
      const shopData = response.data as Shop;
      shopConfigState.type = shopData.type;
      shopConfigState.layout = getShopLayout(shopData);
      shopConfigState.advancedOptions = getShopAdvancedOptions(shopData);
      shopConfigState.developerOptions = getShopDeveloperOptions(shopData);
      this.setupTheme(response.data.color, response.data.theme);
    });
  }

  private initSentry() {
    Sentry.init({
      dsn: Env.SENTRY_SHOP_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
      tracesSampleRate: 0,
      tracePropagationTargets: [baseUri()],
      environment: currentEnvironment(),
    });
    Sentry.setExtra('shop_id', this.shopId);
    Sentry.setExtra('group_uuid', this.groupUuid);
    Sentry.setExtra('uuid', this.cart?.uuid);
    Sentry.startSession();
  }

  private loadCustom() {
    const shadowRoot = this.host.shadowRoot;
    this.customElements = [];

    if (shadowRoot === null) {
      console.error('Shadow root not found for this component.');
      return;
    }

    // Add custom CSS file to the shadow root (because of the shadow dom blocking parent styles)
    if (shopConfigState.developerOptions.customCss?.length > 0) {
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = shopConfigState.developerOptions.customCss;
      shadowRoot.appendChild(cssLink);
    }

    // Add custom HTML elements to the head or to the top of the ike-layout
    if (shopConfigState.developerOptions.customHtml?.length > 0) {
      const customHTML = shopConfigState.developerOptions.customHtml;
      const tempContainer = document.createElement("div");
      tempContainer.innerHTML = customHTML;

      const headTags = ["META", "LINK", "STYLE", "SCRIPT"];
      const headElement = document.head;

      const customElements = [];

      Array.from(tempContainer.children).forEach((node) => {
        if (headTags.includes(node.tagName)) {
          // Append to the <head>
          headElement.appendChild(node);
        } else {
          // Append to the ike-layout if in embedded, or after the head if in portal (same as before shop rework)
          if (shopIntegrationState.isEmbedded) {
            customElements.push(node.outerHTML);
          } else {
            headElement.after(node);
          }
        }
      });

      this.customElements = customElements;
    }
  }

  componentDidLoad() {
    this.resizeObserver();
    this.utils.store.set('shopComponent', this.layout);
  }

  disconnectedCallback() {
    this.unsub.forEach(call => call());
    Sentry.endSession();
    homeStore.reset();
    shopConfig.reset();
  }

  @Listen('ike_loading')
  loadingHandler() {
    this.loading = true;
    setTimeout(() => this.loading = false, 1000);
  }

  @Listen('ike_scroll_to_top')
  scrollHandler() {
    this.onClickButtonHandler({detail: 0});
  }

  @Listen('ike_scroll_to')
  onClickButtonHandler(data) {
    if (!shopIntegrationState.isEmbedded) {
      window.scrollTo({
        top: data.detail,
        behavior: 'smooth',
      });
    } else {
      this.layout.scrollTo({
        top: data.detail,
        behavior: 'smooth',
      });
    }
  }

  @Listen('ike_notification', { target: 'document' })
  notificationHandler(event) {
    const [type, title, msg] = event.detail;
    const notification = { type, title, msg };
    if (!this.notifications.find(notif => notif.msg == notification.msg)) {
      this.notifications = [...this.notifications, notification];
    }
    setTimeout(() => {
      this.notifications = this.notifications.filter(n => n !== notification);
    }, 3 * 1000);

    if (type === 'error') {
      document.dispatchEvent(new CustomEvent('ike_notification_exception', {
        detail: {
          exception: title + ' ' + (msg ?? ''),
          path: this.utils.route.path,
        },
      }));
    }
  }

  @Listen('getUtils')
  getUtilsHandler(event) {
    event.detail(this.utils);
  }

  private handlePageEl(el) {
    setTimeout(() => {
      if (this.pageEl !== el) {
        this.firstComp();
        if (!this.pageEl && el) {
          this.loading = false;
        }
        this.pageEl = el;
      }
    }, 1);
  }

  @Watch('interfaceLanguage')
  private changeInterfaceLanguage(fullReload = true) {
    language(this.interfaceLanguage);
    if (fullReload) {
      this.getConfig();
      this.getCart();
    }
  }

  private handleProgressBar(el) {
    if (el) {
      setInterval(() => {
        el.style.width = '0%';
      }, 5);
    }
  }

  private loadFonts() {
    return Promise.all(this.fonts.map(font =>
      new FontFace(font.name, font.url, {
        weight: font.weight,
      }).load()
        .then(e => (document.fonts as any).add(e))
        .catch(err => console.log(err)),
    ));
  }

  private shouldGoToCheckout() {
    const cart = this.utils.store.get('cart');
    if (cart?.isEditable === false
        && cart?.isPaid === false
        && this.route.path !== '/checkout'
        && !this.route.path.includes('/success'))
    {
      this.utils.goTo('/checkout');
      this.notificationHandler({ detail: ['error', translate('Vous devez payer ou annuler votre commande')] });
    }
  }

  private shouldGoToPage() {
    if (this.goToRoute && this.route.path !== this.goToRoute) {
      this.utils.goTo(this.goToRoute);
    }
  }

  private createRouter() {
    return new Promise<void>(resolve => {
      const router = createRouter({
        basePath: this.basePath !== null ? this.basePath : '',
        engine: this.basePath !== null
          ? BrowserHistoryEngine({bindClick: false})
          : ArrayHistoryEngine(),
      });

      this.routes.forEach(route => {
        router.get(route.path, req => {
          this.utils.observable.dispatchEvent(new CustomEvent('routeChanged', { detail: { route, req } }));
          this.utils.route = req;
          this.route = route;
          this.shouldGoToCheckout();
          req.stop();
        });
      });

      router.always(() => {
        resolve();
        this.loading = true;
        if (!shopIntegrationState.isEmbedded) {
          this.host.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });

      this.utils.router = router.run();
    });
  }

  private resizeObserver() {
    const ro = new ResizeObserver(entries => {
      this.breakpoint = [
        ['SM', 425],
        ['MD', 885],
        ['LG', 1200],
      ].filter(
        ([, width]: [string, number]) => width > entries[0].contentRect.width,
      ).map(
        ([name]: [string, number]) => name,
      ).join(' ');
      this.utils.storage.set('breakpoint', this.breakpoint);
    });
    ro.observe(this.layout);
  }

  private setupCustomPortal(customPortalId: number | null): void {
    if (customPortalId !== null && customPortalExists(customPortalId)) {
      customPortalState.id = customPortalId;
      if (!this.utils.storage.get('browser_uuid')) {
        this.utils.storage.set('browser_uuid', uuidv4());
      }
    }
  }

  private setupTheme(color: string | null, theme: ShopTheme | null, force = false) {
    if ((!themeState.color || force) && color) {
      themeState.color = color;
    }
    if ((!themeState.theme || force) && theme) {
      themeState.theme = theme;
    }
  }

  private renderCustomStyle() {
    const maincolor = tinycolor(themeState.color);
    const storageEndpoint = Env.STORAGE_URL || 'https://etickets.storage5.infomaniak.com';
    return <style>
      {`
        .layout {
          --main-color: ${themeState.color} !important;
          --main-color-text: ${maincolor.getLuminance() > .6 ? '#333333' : '#ffffff'} !important;
          --main-color-contrasted: ${maincolor.getLuminance() > .1 ? maincolor.darken(20) : maincolor.lighten(20)} !important;
          --main-color-contrasted-hover: ${maincolor.darken(10)} !important;

          --flags-url: url(${storageEndpoint}/images/flags.png);
          --flags-2-url: url(${storageEndpoint}/images/flags@2x.png);
        }
      `}
    </style>;
  }

  private renderCustomHTML() {
    return this.customElements.map((html, index) => (
      <div key={index} innerHTML={html}></div>
    ));
  }

  private renderContent() {

    // Waiting list (queue)
    if (this.showQueue || this.route?.component == 'ike-queue') {
      return <ike-queue ref={el => this.handlePageEl(el)} class={{ 'loading': this.loadingFont }} />;
    }

    // Cashless
    if (this.route?.component == 'ike-order-cashless') {
      return [
        <ike-navbar lite={true}/>,
        <ike-order-cashless theme={themeState.theme} ref={el => this.handlePageEl(el)}
                            class={{'loading': this.loadingFont}}/>,
        <ike-footer class={{"loading": this.loadingFont, 'lite': true}}/>
      ];
    }

    // Period listing
    if (this.isMultiPeriodSwitchEnabled && this.shopId === null) {
      return [
        <div class="period-listing">
          <h1 class="title-2">{translate('Sélectionner une billetterie')}</h1>
          <ike-default-shops groupUuid={this.groupUuid} ref={el => this.handlePageEl(el)}
                             class={{'loading': this.loadingFont}}/>
        </div>];
    }

    // VOD
    if (this.route?.component == 'ike-event-stream') {
      return <ike-event-stream ref={el => this.handlePageEl(el)} class={{ 'loading': this.loadingFont }} />
    }

    return [
      this.cart && !shopIntegrationState.isProfile &&
      <ike-navbar multiPeriodSwitch={this.isMultiPeriodSwitchEnabled} class={{'loading': this.loadingFont}}/>,

      this.route &&
      <this.route.component draft={this.draft} ref={el => this.handlePageEl(el)} preventSingleArticleRedirection={this.preventSingleArticleRedirection}
                            class={{'loading': this.loadingFont}}/>,
      <ike-footer class={{"loading": this.loadingFont, 'lite': false}}/>
    ];
  }

  private renderDemoBanner() {
    if (this.showDemoAlert) {
      return <ike-demo-alert layout={this.layout} ref={el => this.handlePageEl(el)}
                             class={{ 'loading': this.loadingFont }} />;
    }
  }

  private renderNotifications() {
    return <div class={{ 'notifications': true, 'show-notif': this.notifications.length > 0 }}>
      {this.notifications.map(notification =>
        <div class={notification.type}>
          <div class="flex space-between align-items-center full-width">
            {notification.title &&
              <div class="notification-content">
                <i
                  class={notification.type === 'error' ? 'icon-alert-triangle' : notification.type === 'success' ? 'icon-check-circle' : 'icon-alert-triangle'} />
                <div>
                  <span>{notification.title}</span>
                  {notification.msg && [
                    <div class="notification-message">{notification.msg}</div>,
                  ]}
                </div>
              </div>
            }
            <div class="notification-close"><a
              onClick={() => this.notifications = this.notifications.filter(n => n !== notification)}><i
              class="icon-close" /></a></div>
          </div>
          <div class="rab_ssergorp" ref={el => this.handleProgressBar(el)} />
          <div class="rab_ssergorp_bg" />
        </div>,
      )}
    </div>;
  }

  render() {
    return <div class={{
      'layout': true,
      [themeState.theme]: true,
      [this.breakpoint]: true,
      'is-portal': !shopIntegrationState.isEmbedded,
      'EMBEDED': shopIntegrationState.isEmbedded,
      'PROFILE': shopIntegrationState.isProfile,
      'showPeriodList': this.showPeriodList,
      'iti-mobile': this.utils.isItiMobile(),
    }} ref={el => this.layout = el}>
      {this.renderCustomStyle()}
      {this.renderCustomHTML()}
      {this.loadingFont ? <ike-page-loader color={this.color}/> :
        <div class={{ 'loadingBar': true, 'loadingBar__full': this.loading }} />}
      {!this.loadingFirst && this.renderDemoBanner()}
      {!this.loadingFirst && this.renderContent()}
      {this.renderNotifications()}
      <div ref={el => this.utils.store.set('datepickerContainer', el)} id="datepicker" />
    </div>;
  }
}
